import { FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import {
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  useStudentDocumentsActions,
  useStudentDocumentsState,
} from '../../contexts/studentDocuments.context';
import { GridColDef } from '@mui/x-data-grid';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import { useStudentFiltersState } from '../../contexts/studentFilters.context';
import { values, isEmpty } from 'lodash-es';
import { GRADE } from '../../../../../apis/admissions/admissions.interfaces';
import {
  TDocument,
  TDocumentStatus,
  TDocumentType,
  TUserDocumentSummaryMap,
} from '../../../../../apis/documents/documents.interfaces';
import { TUserAction } from '../../../../../apis/__shared/shared.interfaces';
import { useTranslation } from '../../../../../hooks/useTranslationHook';
import { AddButton, EditButton } from 'src/views/components/simple/AllButtons';
import StudentManageDocumentView from './studentManageDocument.view';
import DocumentDetailsComponent from '../../../../components/documentDetails/documentDetails.component';
import { PermissionGate } from '../../../../../components/rbac/PermissionGate';

const GRADE1_MOCK_DOC_UPLOAD: TUserAction = {
  userId: 'mock-doc-system',
  timestamp: 1704067200000,
};

const createGrade1MockUserDocumentRow = (args: {
  documentId: string;
  userId: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  docType: TDocumentType;
  status: TDocumentStatus;
  submissionDate: string;
  verificationDate?: string;
}): TUserDocumentSummaryMap => {
  const doc: TDocument = {
    documentId: args.documentId,
    userId: args.userId,
    documentType: args.docType,
    documentName: `${args.docType}-grade1-sample.pdf`,
    submissionDate: args.submissionDate,
    status: args.status,
    verificationDate: args.verificationDate,
    uploadedAt: GRADE1_MOCK_DOC_UPLOAD,
  };
  return {
    id: args.userId,
    basicDetails: {
      firstName: args.firstName,
      middleName: args.middleName,
      lastName: args.lastName,
    },
    documents: [doc],
  };
};

/** Dev preview rows when Grade 1 is selected (aligned with student filters section). */
const GRADE_1_MOCK_DOCS_WITH_SECTION: {
  section: string;
  row: TUserDocumentSummaryMap;
}[] = [
  { section: 'A', row: createGrade1MockUserDocumentRow({ userId: 'mock-s1', documentId: 'mock-g1-doc-1', firstName: 'Aarav', lastName: 'Sharma', docType: TDocumentType.BIRTH_CERTIFICATE, status: TDocumentStatus.VERIFIED, submissionDate: '2025-02-01', verificationDate: '2025-02-03' }) },
  { section: 'A', row: createGrade1MockUserDocumentRow({ userId: 'mock-s2', documentId: 'mock-g1-doc-2', firstName: 'Diya', lastName: 'Patel', docType: TDocumentType.AADHAR_CARD, status: TDocumentStatus.PENDING, submissionDate: '2025-02-04' }) },
  { section: 'B', row: createGrade1MockUserDocumentRow({ userId: 'mock-s3', documentId: 'mock-g1-doc-3', firstName: 'Kabir', lastName: 'Singh', docType: TDocumentType.TRANSFER_CERTIFICATE, status: TDocumentStatus.VERIFIED, submissionDate: '2025-01-20', verificationDate: '2025-01-25' }) },
  { section: 'A', row: createGrade1MockUserDocumentRow({ userId: 'mock-s4', documentId: 'mock-g1-doc-4', firstName: 'Ishaan', lastName: 'Verma', docType: TDocumentType.PREVIOUS_MARKSHEET, status: TDocumentStatus.VERIFIED, submissionDate: '2025-02-10', verificationDate: '2025-02-11' }) },
  { section: 'A', row: createGrade1MockUserDocumentRow({ userId: 'mock-s5', documentId: 'mock-g1-doc-5', firstName: 'Myra', lastName: 'Iyer', docType: TDocumentType.MEDICAL_CERTIFICATE, status: TDocumentStatus.REJECTED, submissionDate: '2025-01-15', verificationDate: undefined }) },
  { section: 'B', row: createGrade1MockUserDocumentRow({ userId: 'mock-s6', documentId: 'mock-g1-doc-6', firstName: 'Vihaan', lastName: 'Reddy', docType: TDocumentType.PHOTO, status: TDocumentStatus.SUBMITTED, submissionDate: '2025-02-12' }) },
  { section: 'B', row: createGrade1MockUserDocumentRow({ userId: 'mock-s7', documentId: 'mock-g1-doc-7', firstName: 'Anaya', lastName: 'Krishnan', docType: TDocumentType.BIRTH_CERTIFICATE, status: TDocumentStatus.VERIFIED, submissionDate: '2025-01-08', verificationDate: '2025-01-10' }) },
  { section: 'C', row: createGrade1MockUserDocumentRow({ userId: 'mock-s8', documentId: 'mock-g1-doc-8', firstName: 'Reyansh', lastName: 'Nair', docType: TDocumentType.AADHAR_CARD, status: TDocumentStatus.PENDING, submissionDate: '2025-02-14' }) },
  { section: 'C', row: createGrade1MockUserDocumentRow({ userId: 'mock-s9', documentId: 'mock-g1-doc-9', firstName: 'Kavya', lastName: 'Menon', docType: TDocumentType.PREVIOUS_MARKSHEET, status: TDocumentStatus.VERIFIED, submissionDate: '2025-02-02', verificationDate: '2025-02-05' }) },
  { section: 'A', row: createGrade1MockUserDocumentRow({ userId: 'mock-s10', documentId: 'mock-g1-doc-10', firstName: 'Arjun', lastName: 'Desai', docType: TDocumentType.MEDICAL_CERTIFICATE, status: TDocumentStatus.VERIFIED, submissionDate: '2025-01-28', verificationDate: '2025-01-30' }) },
  { section: 'B', row: createGrade1MockUserDocumentRow({ userId: 'mock-s11', documentId: 'mock-g1-doc-11', firstName: 'Pari', lastName: 'Choudhary', docType: TDocumentType.TRANSFER_CERTIFICATE, status: TDocumentStatus.PENDING, submissionDate: '2025-02-18' }) },
  { section: 'C', row: createGrade1MockUserDocumentRow({ userId: 'mock-s12', documentId: 'mock-g1-doc-12', firstName: 'Yuvan', lastName: 'Bose', docType: TDocumentType.BIRTH_CERTIFICATE, status: TDocumentStatus.VERIFIED, submissionDate: '2025-01-05', verificationDate: '2025-01-07' }) },
  { section: 'C', row: createGrade1MockUserDocumentRow({ userId: 'mock-s13', documentId: 'mock-g1-doc-13', firstName: 'Zara', lastName: 'Khan', docType: TDocumentType.AADHAR_CARD, status: TDocumentStatus.REJECTED, submissionDate: '2025-02-20' }) },
];

interface StudentDocumentsGridProps {
  isLoading?: boolean;
}

const StudentDocumentsGrid: FC<StudentDocumentsGridProps> = () => {
  const { translateText } = useTranslation();
  const {
    studentDocumentRecords,
    simplifiedStudentObject,
    studentDocumentsAppState: { isManageDocumentOpen, selectedDocumentId },
  } = useStudentDocumentsState();
  const filters = useStudentFiltersState();
  const {
    setIsManageDocumentOpen,
    setSelectedDocumentId,
    setIsCreateMode,
    setIsEditMode,
    setSelectedDocument,
  } = useStudentDocumentsActions();

  const [documentsViewType, setDocumentsViewType] = useState<
    TDocumentStatus | 'all'
  >('all');
  const [filteredDocuments, setFilteredDocuments] = useState<
    TUserDocumentSummaryMap[]
  >([]);

  const handleDocumentsViewChange = (
    _: MouseEvent<HTMLElement>,
    newViewType: TDocumentStatus | 'all'
  ) => {
    setDocumentsViewType(newViewType);
  };

  const onAddDocumentClickHandler = () => {
    setIsManageDocumentOpen(true);
    setIsCreateMode(true);
    setIsEditMode(false);
  };

  const onEditDocumentClickHandler = () => {
    setIsManageDocumentOpen(true);
    setIsCreateMode(false);
    setIsEditMode(true);
  };

  const displayDocumentRows = useMemo(() => {
    if (filters.grade === GRADE.ONE) {
      const list =
        filters.section && filters.section !== 'select'
          ? GRADE_1_MOCK_DOCS_WITH_SECTION.filter(
            (x) => x.section === filters.section
          )
          : GRADE_1_MOCK_DOCS_WITH_SECTION;
      return list.map((x) => x.row);
    }
    return filteredDocuments;
  }, [filters.grade, filters.section, filteredDocuments]);

  const handleRowClick = (params: any) => {
    const document = displayDocumentRows.find((doc) => doc.id === params.row.id);
    if (document && document.documents[0]) {
      setSelectedDocumentId(document.documents[0].documentId);
      setSelectedDocument(document.documents[0]);
    }
  };

  const handleBackToDocuments = () => {
    setSelectedDocumentId('');
    setSelectedDocument(null);
    setIsCreateMode(false);
    setIsEditMode(false);
    setIsManageDocumentOpen(false);
  };

  const getDocumentsGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'basicDetails.name',
        headerName: translateText('students.documents.studentName'),
        width: 200,
        renderCell: (params) => {
          const data = params.row as TUserDocumentSummaryMap;
          return `${data.basicDetails.firstName} ${data.basicDetails.middleName || ''} ${data.basicDetails.lastName}`;
        },
      },
      {
        field: 'documentType',
        headerName: translateText('students.documents.documentType'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TUserDocumentSummaryMap;
          return data.documents[0]?.documentType;
        },
      },
      {
        field: 'submissionDate',
        headerName: translateText('students.documents.submissionDate'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TUserDocumentSummaryMap;
          return data.documents[0]?.submissionDate;
        },
      },
      {
        field: 'status',
        headerName: translateText('common.status'),
        width: 120,
        renderCell: (params) => {
          const data = params.row as TUserDocumentSummaryMap;
          return data.documents[0]?.status;
        },
      },
      {
        field: 'verificationDate',
        headerName: translateText('common.verificationDate'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TUserDocumentSummaryMap;
          return data.documents[0]?.verificationDate || '-';
        },
      },
    ];
  };

  useEffect(() => {
    const getDocumentsGridRowsToRender = () => {
      if (isEmpty(studentDocumentRecords)) return [];

      const documentMap: Record<string, TUserDocumentSummaryMap> = {};

      studentDocumentRecords.forEach((eachStudentDoc) => {
        const studentDetails = simplifiedStudentObject[eachStudentDoc.userId];
        if (
          (!filters.grade ||
            filters.grade === 'select' ||
            studentDetails?.grade === filters.grade) &&
          (!filters.section ||
            filters.section === 'select' ||
            studentDetails?.section === filters.section)
        ) {
          if (!documentMap[eachStudentDoc.userId]) {
            documentMap[eachStudentDoc.userId] = {
              id: eachStudentDoc.userId,
              basicDetails: {
                firstName: studentDetails.basicDetails.firstName,
                middleName: studentDetails.basicDetails.middleName ?? undefined,
                lastName: studentDetails.basicDetails.lastName,
              },
              documents: [eachStudentDoc],
            };
          } else {
            documentMap[eachStudentDoc.userId].documents.push(eachStudentDoc);
          }
        }
      });

      setFilteredDocuments(values(documentMap));
    };

    getDocumentsGridRowsToRender();
  }, [studentDocumentRecords, simplifiedStudentObject, filters]);

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2 }}
      data-testid="student-documents-grid-content"
      sx={{ height: '100%', overflowY: 'auto' }}
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 2 },
          mb: { xs: 1, sm: 2 },
        }}
      >
        {selectedDocumentId ? (
          <>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              gap={{ xs: 1, sm: 2 }}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBackToDocuments}
                sx={{
                  minHeight: { xs: '44px', sm: '36px' },
                  fontSize: { xs: '16px', sm: '14px' },
                }}
                data-testid="back-to-documents-button"
              >
                {translateText('students.documents.backToDocuments')}
              </Button>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                }}
                data-testid="document-details-title"
              >
                {translateText('students.documents.documentDetails')}
              </Typography>
            </Box>
            <PermissionGate
              module="students"
              submodule="documents"
              permission="write"
            >
              <EditButton
                label={translateText('students.documents.editDocument')}
                onClickHandler={onEditDocumentClickHandler}
                data-testid="edit-document-button"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                }}
              />
            </PermissionGate>
          </>
        ) : (
          <ToggleButtonGroup
            size="medium"
            color="primary"
            exclusive
            value={documentsViewType}
            onChange={handleDocumentsViewChange}
            aria-label={translateText('students.documents.documentsView')}
            sx={{
              height: { xs: '44px', sm: 'var(--tab-group-height)' },
              width: { xs: '100%', sm: 'auto' },
              '& .MuiToggleButton-root': {
                minHeight: { xs: '44px', sm: '36px' },
                fontSize: { xs: '14px', sm: '13px' },
                px: { xs: 1, sm: 1.5 },
              },
            }}
            data-testid="student-documents-view-toggle-button-group"
          >
            <ToggleButton
              value="all"
              data-testid="student-documents-view-toggle-button-all"
            >
              {translateText('students.documents.allDocuments')}
            </ToggleButton>
            <ToggleButton
              value={TDocumentStatus.PENDING}
              data-testid="student-documents-view-toggle-button-pending"
            >
              {translateText('students.documents.pending')}
            </ToggleButton>
            <ToggleButton
              value={TDocumentStatus.VERIFIED}
              data-testid="student-documents-view-toggle-button-verified"
            >
              {translateText('students.documents.verified')}
            </ToggleButton>
            <ToggleButton
              value={TDocumentStatus.REJECTED}
              data-testid="student-documents-view-toggle-button-rejected"
            >
              {translateText('students.documents.rejected')}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        {!selectedDocumentId && (
          <PermissionGate
            module="students"
            submodule="documents"
            permission="write"
          >
            <AddButton
              label={translateText('students.documents.addDocument')}
              onClickHandler={onAddDocumentClickHandler}
              data-testid="student-documents-add-button"
              sx={{
                width: { xs: '100%', sm: 'auto' },
              }}
            />
          </PermissionGate>
        )}
      </Grid>
      {selectedDocumentId ? (
        <Grid item xs={12}>
          <DocumentDetailsComponent documentId={selectedDocumentId} />
        </Grid>
      ) : (
        <Grid
          item
          xs={12}
          sx={{
            height: `calc(100% - var(--tab-group-height))`,
            overflow: 'auto',
          }}
        >
          <PaginatedGrid
            rows={displayDocumentRows.filter((doc) => {
              if (documentsViewType === 'all') return true;
              return doc.documents[0]?.status === documentsViewType;
            })}
            columns={getDocumentsGridColumns()}
            dataTestId="student-documents-grid"
            onRowClick={handleRowClick}
          />
        </Grid>
      )}

      {isManageDocumentOpen && <StudentManageDocumentView />}
    </Grid>
  );
};

export default StudentDocumentsGrid;
